import React, { useState } from 'react';
import { useEvents, useCasts, useGuests, useMatchingResults } from '../hooks/useFirebase';
import { runMatching, runRaffle, runOpen, runCastInstance } from '../services/matchingService';
import { useSchedule, todayStr } from '../hooks/useSchedule';
import { useConfirm } from '../contexts/ConfirmContext';

const MODE_LABELS = {
  cast_select:  { label: 'キャスト選択',     icon: '🎭' },
  guest_select: { label: 'ゲスト選択',       icon: '👤' },
  both_select:  { label: '両想いマッチング', icon: '💞' },
  random_only:  { label: 'ランダム抽選',     icon: '🎲' },
};

// ─── Pre-flight check item ────────────────────────────────────────────────────
const CheckItem = ({ ok, text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
    <span style={{
      width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: ok ? '#ecfdf5' : '#fef2f2',
      border: `1.5px solid ${ok ? '#6ee7b7' : '#fca5a5'}`,
      fontSize: '11px', fontWeight: '900',
      color: ok ? '#10b981' : '#ef4444',
    }}>
      {ok ? '✓' : '!'}
    </span>
    <span style={{ color: ok ? '#475569' : '#64748b' }}>{text}</span>
  </div>
);

// ─── Stat chip ────────────────────────────────────────────────────────────────
const StatChip = ({ value, label, color }) => (
  <div style={{
    display: 'flex', alignItems: 'baseline', gap: '4px',
    padding: '6px 14px',
    background: '#fff', border: '1px solid #e8edf5',
    borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  }}>
    <span style={{ fontSize: '20px', fontWeight: '900', color, lineHeight: 1 }}>{value}</span>
    <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>{label}</span>
  </div>
);

// ─── Section header for session grouping ─────────────────────────────────────
const SessionHeader = ({ session, count, label }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 10,
  }}>
    <span style={{
      padding: '3px 12px', borderRadius: 999,
      background: '#6c63ff', color: '#fff',
      fontSize: 12, fontWeight: 800,
    }}>
      第{session}部
    </span>
    <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>
      {count}{label}
    </span>
    <div style={{ flex: 1, height: 1, background: '#e8edf5' }} />
  </div>
);

// ─── Result card (matching) ───────────────────────────────────────────────────
const ResultCard = ({ pair, cast, guest }) => {
  const isDraft = pair.match_type === 'draft';
  return (
    <div style={{
      background: '#fff', borderRadius: '12px',
      border: '1px solid #e8edf5',
      borderLeft: `3px solid ${isDraft ? '#6c63ff' : '#94a3b8'}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px 8px', borderBottom: '1px solid #f1f5f9' }}>
        <span style={{ fontSize: '13px', fontWeight: '900', color: '#6c63ff', letterSpacing: '0.5px' }}>Room {pair.room_number}</span>
        <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '999px', background: isDraft ? '#ede9fe' : '#f1f5f9', color: isDraft ? '#5b21b6' : '#64748b' }}>
          {isDraft ? `指名 第${pair.round}希望` : 'ランダム'}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 14px' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0, background: '#6c63ff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '14px', overflow: 'hidden' }}>
            {cast?.image_url ? <img src={cast.image_url} alt={cast.vrchat_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (cast?.vrchat_name || cast?.name)?.charAt(0)?.toUpperCase() ?? '?'}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '600', marginBottom: '1px' }}>キャスト</div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cast?.vrchat_name || cast?.name || '—'}</div>
          </div>
        </div>
        <div style={{ flexShrink: 0, color: '#cbd5e1', fontSize: '12px', fontWeight: '700' }}>×</div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, justifyContent: 'flex-end', flexDirection: 'row-reverse' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0, background: '#10b981', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '14px', overflow: 'hidden' }}>
            {guest?.image_url ? <img src={guest.image_url} alt={guest.vrchat_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : guest?.vrchat_name?.charAt(0)?.toUpperCase() ?? '?'}
          </div>
          <div style={{ minWidth: 0, textAlign: 'right' }}>
            <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '600', marginBottom: '1px' }}>ゲスト</div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{guest?.vrchat_name ?? '—'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Raffle result card ───────────────────────────────────────────────────────
const RaffleGuestCard = ({ guest, won, isPriority, isOpen }) => (
  <div style={{
    background: '#fff', borderRadius: '12px',
    border: `1px solid ${isPriority ? '#fde68a' : won ? '#a7f3d0' : '#e2e8f0'}`,
    borderLeft: `3px solid ${isPriority ? '#f59e0b' : won ? '#10b981' : '#cbd5e1'}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px',
  }}>
    <div style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0, background: won ? (isPriority ? '#f59e0b' : '#10b981') : '#e2e8f0', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '14px', overflow: 'hidden' }}>
      {guest?.image_url ? <img src={guest.image_url} alt={guest.vrchat_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : guest?.vrchat_name?.charAt(0)?.toUpperCase() ?? '?'}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{guest?.vrchat_name ?? '—'}</div>
    </div>
    <span style={{ fontSize: '11px', fontWeight: '800', padding: '3px 10px', borderRadius: '999px', background: isPriority ? '#fffbeb' : won ? '#ecfdf5' : '#f1f5f9', color: isPriority ? '#92400e' : won ? '#059669' : '#94a3b8', whiteSpace: 'nowrap' }}>
      {isPriority ? '⭐ 優先当選' : won ? (isOpen ? '✓ 先着当選' : '✓ 当選') : '落選'}
    </span>
  </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
const MatchingPage = ({ selectedEventId }) => {
  const { events } = useEvents();
  const { casts } = useCasts(selectedEventId);
  const { guests, updateGuest } = useGuests(selectedEventId);
  const { saveResults, clearResults } = useMatchingResults(selectedEventId);
  const { schedule } = useSchedule(selectedEventId);
  const [isRunning, setIsRunning] = useState(false);
  const [hovRun, setHovRun] = useState(false);
  const [hovReset, setHovReset] = useState(false);
  const confirm = useConfirm();

  const selectedEvent      = selectedEventId ? events[selectedEventId] : null;
  const eventType          = selectedEvent?.event_type || 'matching';
  const isMatchingType     = eventType === 'matching';
  const isCastInstanceType = eventType === 'cast_instance';

  const sessions           = schedule.sessions ?? null;
  const session_attendance = schedule.session_attendance || {};
  const today              = todayStr();

  // 今日分のsession_attendanceを { [castId]: { [ses]: status } } に整形
  const sessionAttToday = sessions
    ? Object.fromEntries(
        Object.entries(session_attendance).map(([castId, dates]) => [
          castId, dates[today] || {},
        ])
      )
    : null;

  const handleRunMatching = async () => {
    if (!selectedEventId) return;
    if (!(await confirm({ message: '抽選を実行しますか？', type: 'info' }))) return;
    setIsRunning(true);

    let results;
    if (eventType === 'cast_instance') {
      results = runCastInstance(
        casts,
        selectedEvent?.instance_count ?? null,
        selectedEvent?.instance_capacity ?? null,
        sessions,
        sessionAttToday
      );
      // リーダー不足: 警告して中止（保存しない）
      if (results?.error === 'insufficient_leaders') {
        const lines = results.shortfalls.map((s) =>
          s.session
            ? `・第${s.session}部：リーダー ${s.have}人／必要 ${s.need}人`
            : `・リーダー ${s.have}人／必要 ${s.need}人`
        );
        await confirm({
          message:
            `インスタンスリーダーが不足しているため抽選できません。\n\n` +
            `${lines.join('\n')}\n\n` +
            `リーダーを追加するか、インスタンス数を減らしてから再度実行してください。`,
          type: 'warning',
        });
        setIsRunning(false);
        return;
      }
    } else if (eventType === 'raffle') {
      results = runRaffle(guests, selectedEvent?.capacity ?? 0, sessions);
    } else if (eventType === 'open') {
      results = runOpen(guests, selectedEvent?.capacity ?? 0, sessions);
    } else {
      const mode = selectedEvent?.matching_mode || 'cast_select';
      results = runMatching(casts, guests, mode, sessions, sessionAttToday);
    }
    saveResults(results);

    const matchedSet = new Set((results.pairs ?? []).map(p => p.guest_id));
    Object.keys(guests).forEach((guestId) => {
      if (matchedSet.has(guestId)) {
        updateGuest(guestId, { status: 'matched', match_result: results.pairs.find(p => p.guest_id === guestId)?.cast_id ?? null });
      } else {
        updateGuest(guestId, { status: 'waiting', match_result: null });
      }
    });

    setIsRunning(false);
  };

  const handleClearResults = async () => {
    if (!(await confirm({ message: '抽選結果をリセットしますか？', type: 'danger' }))) return;
    clearResults();
    Object.keys(guests).forEach((guestId) =>
      updateGuest(guestId, { status: 'waiting', match_result: null })
    );
  };

  if (!selectedEvent) {
    return (
      <div style={{ minHeight: '100%', background: '#f8fafc', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '40px', opacity: 0.2 }}>🎲</span>
        <p style={{ margin: 0, color: '#94a3b8', fontWeight: '600' }}>イベントが選択されていません</p>
      </div>
    );
  }

  const castArray  = Object.entries(casts);
  const guestArray = Object.entries(guests);

  // 部制あり: session_attendance に working があるか未設定のキャストを出勤扱い
  // 部制なし: cast.status で判定
  const workingCasts = sessions
    ? castArray.filter(([castId]) => {
        const att = sessionAttToday?.[castId] || {};
        return Object.values(att).some(s => s === 'working');
      })
    : castArray.filter(([, c]) => c.status === 'working');
  const mode         = selectedEvent?.matching_mode || 'cast_select';
  const modeInfo     = MODE_LABELS[mode] ?? MODE_LABELS.cast_select;
  const capacity     = selectedEvent?.capacity ?? null;

  const pairs      = selectedEvent?.results?.pairs ?? [];
  const losers     = selectedEvent?.results?.losers ?? [];
  const hasResults = pairs.length > 0;
  const draftCount  = pairs.filter(p => p.match_type === 'draft').length;
  const randomCount = pairs.filter(p => p.match_type === 'random').length;

  // Pre-flight checks
  const instanceConfigured = (selectedEvent?.instance_count > 0) || (selectedEvent?.instance_capacity > 0);
  const instanceLabel = selectedEvent?.instance_mode === 'capacity'
    ? `1インスタンス ${selectedEvent?.instance_capacity ?? '未設定'}人`
    : `${selectedEvent?.instance_count ?? '未設定'} インスタンス`;
  const checks = isCastInstanceType
    ? [
        { ok: workingCasts.length >= 1, text: `出勤中のキャストが1人以上いる（現在 ${workingCasts.length}人）` },
        { ok: instanceConfigured,        text: `インスタンス設定がされている（現在: ${instanceLabel}）` },
      ]
    : isMatchingType
    ? [
        { ok: workingCasts.length >= 1, text: `出勤中のキャストが1人以上いる（現在 ${workingCasts.length}人）` },
        { ok: guestArray.length >= 1,   text: `ゲストが1人以上登録されている（現在 ${guestArray.length}人）` },
      ]
    : [
        { ok: guestArray.length >= 1, text: `ゲストが1人以上登録されている（現在 ${guestArray.length}人）` },
        { ok: capacity > 0,           text: `定員が設定されている（現在 ${capacity ?? '未設定'}人）` },
      ];
  const canRun = checks.every(c => c.ok);

  // 結果を部ごとにグループ化
  const pairsBySession = sessions
    ? Array.from({ length: sessions }, (_, i) => {
        const ses = i + 1;
        return { session: ses, pairs: pairs.filter(p => p.session === ses) };
      })
    : null;

  return (
    <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100%', boxSizing: 'border-box' }}>

      {/* ── HEADER ──────────────────────────────────────────────────────── */}
      <div style={{
        background: '#fff', borderRadius: '16px', padding: '22px 26px', marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 6px 20px rgba(0,0,0,0.04)', border: '1px solid #e8edf5',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <p style={{ margin: '0 0 5px', fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              抽選実行
            </p>
            <h1 style={{ margin: '0 0 14px', fontSize: '24px', fontWeight: '900', color: '#0f172a', letterSpacing: '-0.4px' }}>
              {selectedEvent.event_name}
            </h1>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <StatChip value={workingCasts.length} label="出勤キャスト" color="#6c63ff" />
              {!isCastInstanceType && <StatChip value={guestArray.length} label="ゲスト" color="#10b981" />}
              {sessions && <StatChip value={`第${sessions}部制`} label="" color="#f59e0b" />}
              {isCastInstanceType ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: '8px', fontSize: '12px', fontWeight: '700', color: '#6c63ff' }}>
                  <span>🏘️</span><span>{instanceLabel}</span>
                </div>
              ) : isMatchingType ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: '8px', fontSize: '12px', fontWeight: '700', color: '#6c63ff' }}>
                  <span>{modeInfo.icon}</span><span>{modeInfo.label}</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: '8px', fontSize: '12px', fontWeight: '700', color: '#6c63ff' }}>
                  <span>🎲</span>
                  <span>定員 {capacity ?? '—'} 人{sessions ? `/部 (合計 ${(capacity ?? 0) * sessions}人)` : ''}</span>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            {hasResults && (
              <button
                onMouseEnter={() => setHovReset(true)}
                onMouseLeave={() => setHovReset(false)}
                onClick={handleClearResults}
                style={{
                  padding: '10px 18px', borderRadius: '10px',
                  border: `1px solid ${hovReset ? '#fca5a5' : '#fecaca'}`,
                  background: hovReset ? '#fef2f2' : '#fff',
                  color: '#ef4444', fontSize: '13px', fontWeight: '700',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                結果をリセット
              </button>
            )}
            <button
              onMouseEnter={() => setHovRun(true)}
              onMouseLeave={() => setHovRun(false)}
              onClick={handleRunMatching}
              disabled={isRunning || !canRun}
              style={{
                padding: '10px 22px', borderRadius: '10px', border: 'none',
                background: !canRun ? '#e2e8f0' : hovRun ? '#5b52e8' : '#6c63ff',
                color: !canRun ? '#94a3b8' : '#fff',
                fontSize: '14px', fontWeight: '800',
                cursor: !canRun ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
                boxShadow: canRun && hovRun ? '0 4px 14px rgba(108,99,255,0.4)' : canRun ? '0 2px 8px rgba(108,99,255,0.25)' : 'none',
                transform: canRun && hovRun ? 'translateY(-1px)' : 'none',
              }}
            >
              {isRunning ? '抽選中...' : '🎲 抽選を実行する'}
            </button>
          </div>
        </div>
      </div>

      {/* ── PRE-FLIGHT / RESULTS ────────────────────────────────────────── */}
      {!hasResults ? (
        <div style={{
          background: '#fff', borderRadius: '16px', border: '1px solid #e8edf5',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 6px 20px rgba(0,0,0,0.04)', overflow: 'hidden',
        }}>
          <div style={{ height: '3px', background: canRun ? 'linear-gradient(90deg, #6c63ff, #10b981)' : '#e2e8f0' }} />
          <div style={{ padding: '24px 28px' }}>
            <p style={{ margin: '0 0 16px', fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              抽選実行前のチェック
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
              {checks.map((c, i) => <CheckItem key={i} {...c} />)}
            </div>
            {canRun ? (
              <div style={{ padding: '16px 20px', borderRadius: '12px', background: '#f5f3ff', border: '1px solid #c4b5fd', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>✅</span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#5b21b6' }}>
                  準備が整っています。右上の「抽選を実行する」ボタンを押してください。
                </span>
              </div>
            ) : (
              <div style={{ padding: '16px 20px', borderRadius: '12px', background: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>⚠️</span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#9a3412' }}>
                  上記のチェック項目を確認してから抽選を実行してください。
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{
          background: '#fff', borderRadius: '16px', border: '1px solid #e8edf5',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 6px 20px rgba(0,0,0,0.04)', overflow: 'hidden',
        }}>
          <div style={{ height: '3px', background: 'linear-gradient(90deg, #6c63ff, #10b981)' }} />
          <div style={{ padding: '20px 24px' }}>

            {/* Results summary */}
            <div style={{ marginBottom: '20px' }}>
              <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                {isMatchingType ? 'マッチング結果' : '抽選結果'}
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {isCastInstanceType ? (
                  <>
                    <StatChip value={pairs.length} label="キャスト割り振り済み" color="#6c63ff" />
                    <StatChip value={[...new Set(pairs.map(p => p.instance_number))].length} label="インスタンス" color="#10b981" />
                  </>
                ) : isMatchingType ? (
                  <>
                    <StatChip value={pairs.length}  label="組マッチ(合計)"  color="#6c63ff" />
                    <StatChip value={draftCount}    label="指名マッチ" color="#10b981" />
                    <StatChip value={randomCount}   label="ランダム"   color="#f59e0b" />
                  </>
                ) : (
                  <>
                    <StatChip value={pairs.length}  label="当選(合計)"  color="#10b981" />
                    <StatChip value={losers.length} label="落選"        color="#94a3b8" />
                  </>
                )}
              </div>
            </div>

            {/* Cards grid — matching */}
            {isMatchingType && (
              sessions ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {pairsBySession.map(({ session, pairs: sesPairs }) => (
                    <div key={session}>
                      <SessionHeader session={session} count={sesPairs.length} label="組" />
                      {sesPairs.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                          {sesPairs.map((pair, idx) => (
                            <ResultCard key={`${pair.cast_id}-${pair.guest_id}-${idx}`} pair={pair} cast={casts[pair.cast_id]} guest={guests[pair.guest_id]} />
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>この部の組み合わせはありません</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                  {pairs.map((pair, idx) => (
                    <ResultCard key={`${pair.cast_id}-${pair.guest_id}-${idx}`} pair={pair} cast={casts[pair.cast_id]} guest={guests[pair.guest_id]} />
                  ))}
                </div>
              )
            )}

            {/* Cards grid — cast_instance */}
            {isCastInstanceType && (() => {
              const instanceNums = [...new Set(pairs.map(p => p.instance_number))].sort((a, b) => a - b);
              const renderInstanceGroup = (instNum, instPairs) => (
                <div key={instNum}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ padding: '3px 12px', borderRadius: 999, background: '#6c63ff', color: '#fff', fontSize: 12, fontWeight: 800 }}>
                      インスタンス {instNum}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>{instPairs.length}人</span>
                    <div style={{ flex: 1, height: 1, background: '#e8edf5' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                    {instPairs.map((pair, idx) => {
                      const cast = casts[pair.cast_id];
                      return (
                        <div key={`${pair.cast_id}-${idx}`} style={{
                          background: '#fff', borderRadius: '12px',
                          border: '1px solid #e8edf5', borderLeft: '3px solid #6c63ff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                        }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0, background: '#6c63ff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '13px', overflow: 'hidden' }}>
                            {cast?.image_url ? <img src={cast.image_url} alt={cast.vrchat_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (cast?.vrchat_name || cast?.name)?.charAt(0)?.toUpperCase() ?? '?'}
                          </div>
                          <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {cast?.is_instance_leader && <span title="インスタンスリーダー" style={{ flexShrink: 0 }}>👑</span>}
                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {cast?.vrchat_name || cast?.name || '—'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );

              if (sessions) {
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                    {Array.from({ length: sessions }, (_, i) => i + 1).map(ses => {
                      const sesPairs = pairs.filter(p => p.session === ses);
                      if (sesPairs.length === 0) return null;
                      return (
                        <div key={ses}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                            <span style={{ padding: '3px 12px', borderRadius: 999, background: '#f59e0b', color: '#fff', fontSize: 12, fontWeight: 800 }}>第{ses}部</span>
                            <div style={{ flex: 1, height: 1, background: '#e8edf5' }} />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {[...new Set(sesPairs.map(p => p.instance_number))].sort((a,b)=>a-b).map(instNum =>
                              renderInstanceGroup(instNum, sesPairs.filter(p => p.instance_number === instNum))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              }

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {instanceNums.map(instNum =>
                    renderInstanceGroup(instNum, pairs.filter(p => p.instance_number === instNum))
                  )}
                </div>
              );
            })()}

            {/* Cards grid — raffle / open */}
            {!isMatchingType && !isCastInstanceType && (
              sessions ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {pairsBySession.map(({ session, pairs: sesPairs }) => {
                    if (sesPairs.length === 0) return null;
                    return (
                      <div key={session}>
                        <SessionHeader session={session} count={sesPairs.length} label="人当選" />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px' }}>
                          {sesPairs.map((pair, idx) => (
                            <RaffleGuestCard key={`w-${pair.guest_id}-${idx}`} guest={guests[pair.guest_id]} won={true} isPriority={pair.match_type === 'priority'} isOpen={eventType === 'open'} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {losers.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <span style={{ padding: '3px 12px', borderRadius: 999, background: '#e2e8f0', color: '#64748b', fontSize: 12, fontWeight: 800 }}>落選</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8' }}>{losers.length}人</span>
                        <div style={{ flex: 1, height: 1, background: '#e8edf5' }} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px' }}>
                        {losers.map((gid, idx) => (
                          <RaffleGuestCard key={`l-${gid}-${idx}`} guest={guests[gid]} won={false} isPriority={false} isOpen={eventType === 'open'} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px' }}>
                  {pairs.map((pair, idx) => (
                    <RaffleGuestCard key={`w-${pair.guest_id}-${idx}`} guest={guests[pair.guest_id]} won={true} isPriority={pair.match_type === 'priority'} isOpen={eventType === 'open'} />
                  ))}
                  {losers.map((gid, idx) => (
                    <RaffleGuestCard key={`l-${gid}-${idx}`} guest={guests[gid]} won={false} isPriority={false} isOpen={eventType === 'open'} />
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchingPage;
